<i18n>
{
	"en": {
		"autoSearch": "Autosearch every 60 seconds",
		"cargoCarrier": "Cargo Carrier",
		"fromLocation": "From Location",
		"mainDesc": "You can save search settings as a quick search for use in the menu. Select the search criteria to save in the search:",
		"makePublic": "Make this quick search available to other users",
		"save": "Save",
		"searchCriteria": "Search Criteria",
		"searchCriteriaDesc": "Select the criteria you want the shortcut to use:",
		"searchName": "Name",
		"searchNameDesc": "Name of the search displayed in the menu",
		"status": "Status",
		"timeInterval": "Time Interval",
		"timeIntervalAfter": "Today | day after present | days after present",
		"timeIntervalBefore": "Today | day before present | days before present",
		"timeIntervalDesc": "The search will use this time interval by default",
		"title": "Save Quick Search",
		"toLocation": "To Location",
		"tripType": "Trip Type",
		"viewMainMenu": "Show this quick search in the main menu",
		"viewMainMenuDesc": "It is also possible to change this later",
		"cancelLabel": "Avbryt",
		"failedToSave": "Failed to save search",
		"failedToDelete": "Failed to delete search",
		"savedSearch": "Quicksearch saved",
		"deletedSearch": "Quicksearch deleted",
		"noDaySet": "No day set",
		"before": "Before",
		"after": "After",
		"noLabel": "Please give this quick search a name",
		"emptyPatch": "Nothing to update"
	},
	"no": {
		"autoSearch": "Autosøk hvert 60. sekund",
		"cargoCarrier": "Lastebærer",
		"fromLocation": "Fra sted",
		"mainDesc": "Du kan lagre søkeinstillingene som en hurtigsøk for å gjøre dette tilgjengelig i menyen. Velg hvilke av søkekriteriene som skal lagres i hurtigsøket:",
		"makePublic": "Gjøre hurtigsøket tilgjengelig for andre brukere",
		"save": "Lagre",
		"searchCriteria": "Søkekriterier",
		"searchCriteriaDesc": "Velg de kriteriene du vil at hurtigsøket skal bruke:",
		"searchName": "Navn",
		"searchNameDesc": "Navnet på hurtigsøket vises i menyen",
		"status": "Status",
		"timeInterval": "Tidsintervall",
		"timeIntervalAfter": "I dag | dag etter nådato | dager etter nådato",
		"timeIntervalBefore": "I dag | dag før nådato | dager før nådato",
		"timeIntervalDesc": "Søket vil bruke dette tidsintervallet som standard",
		"title": "Lagre hurtigsøk",
		"toLocation": "Til sted",
		"tripType": "Turtype",
		"viewMainMenu": "Vis dette hurtigsøket i hovedmenyen",
		"viewMainMenuDesc": "Det er også mulig å endre dette senere",
		"cancelLabel": "Avbryt",
		"failedToSave": "Kunne ikke lagre søk",
		"failedToDelete": "Kunne ikke slette søk",
		"savedSearch": "Hurtigsøk lagret",
		"deletedSearch": "Hurtigsøk slettet",
		"noDaySet": "Ingen dag satt",
		"before": "Før",
		"after": "Etter",
		"noLabel": "Vennligst gi hurtigsøket et navn",
		"emptyPatch": "Ingen endring gjort"
	}
}
</i18n>

<template>
	<t-dialog
		:isShown="isShown"
		:title="$t('title')"
		:confirmLabel="$t('save')"
		:cancelLabel="$t('cancelLabel')"
		disableCloseOnConfirm
		width="650px"
		@close="onClose"
		@ok="onSaveSearch"
		@shown="processShown"
	>
		<div class="save-search-top">
			<div style="padding-bottom:10px">{{ $t('mainDesc') }}</div>
			<t-text-input
				v-model="newSettings.label"
				:label="$t('searchName')"
				:description="$t('searchNameDesc')"
				width="100%"
				:isRequired="true"
				:isInvalid="missingLabel && !newSettings.label"
				:validationMessage="$t('noLabel')"
			/>
		</div>

		<div class="save-search-mid">
			<TripFilters :tripCriteria="tripCriteria" :tripFilter="newSettings"/>
			<div class="center">
				<div class="search-components">
					<div class="trip-search">
						<div class="save-search-time">
							<t-input-wrapper :label="$t('timeInterval')" :description="$t('timeIntervalDesc')"/>
							<div class="time-interval">
								<t-number-input
									v-model="before"
									:placeholder="$t('noDaySet')"
									:extraDisplayText="$tc(fromDateIsAfterToday ? 'timeIntervalAfter' : 'timeIntervalBefore', parseInt(before))"
									hideZeroValue
									leftAlign
									min="0"
									style="grid-area: input1"
								/>
								<t-switch
									v-model="fromDateIsAfterToday"
									:labelBefore="$t('before')"
									:label="$t('after')"
									:noColor="true"
									style="grid-area: switch1"
								/>
								<t-checkbox
									v-model="tripTimeInterval.useAfter"
									style="grid-area: checkbox"
								/>
								<t-number-input
									v-model="after"
									:placeholder="$t('noDaySet')"
									:extraDisplayText="$tc(toDateIsAfterToday ? 'timeIntervalAfter' : 'timeIntervalBefore', parseInt(after))"
									hideZeroValue
									leftAlign
									min="0"
									style="grid-area: input2"
								/>
								<t-switch
									v-model="toDateIsAfterToday"
									:labelBefore="$t('before')"
									:label="$t('after')"
									:noColor="true"
									style="grid-area: switch2"
								/>
							</div>

							<t-switch
								v-model="newSettings.autoSearch"
								:label="$t('autoSearch')"
							/>
						</div>
						<div class="save-search-criteria">
							<div class="search-criteria-label">{{ $t('searchCriteria') }}</div>
							<div class="search-criteria-description">{{ $t('searchCriteriaDesc') }}</div>

							<div style="display: grid; grid-row-gap: 10px; grid-template-rows: repeat(4, min-content);">
								<t-switch
									v-model="tripCriteria.fromLocation"
									:label="$t('fromLocation')"
									@change="updateTripFilter('fromLocation', $event)"
								/>
								<t-switch
									v-model="tripCriteria.toLocation"
									:label="$t('toLocation')"
									@change="updateTripFilter('toLocation', $event)"
								/>
								<t-switch
									v-model="tripCriteria.tripType"
									:label="$t('tripType')"
									@change="updateTripFilter('tripType', $event)"
								/>
								<t-switch
									v-model="tripCriteria.status"
									:label="$t('status')"
									@change="updateTripFilter('status', $event)"
								/>
								<t-switch
									v-model="tripCriteria.cargoCarrier"
									:label="$t('cargoCarrier')"
									@change="updateTripFilter('cargoCarrier', $event)"
								/>
							</div>
						</div>
					</div>
				</div>

				<div>
					<t-checkbox
						v-model="newSettings.mainMenu"
						:label="$t('viewMainMenu')"
						:description="$t('viewMainMenuDesc')"
					/>
					<t-checkbox
						v-model="newSettings.isPublic"
						:label="$t('makePublic')"
					/>
				</div>
			</div>
		</div>
		<template
			v-if="this.$route.params.searchId"
			#additional-buttons
		>
			<t-icon-button
				intent="danger"
				appearance="primary"
				icon="trash"
				type="bp"
				@click="onDeleteSearch"
			/>
		</template>
	</t-dialog>
</template>
<script>
import { mapActions } from './store/saveTripSearch.store.js';
import { storageDateFormat } from '@/core';
import moment from 'moment';
import deepCopy from 'deepcopy';
import TripFilters from './TripFilters';

export default {
	name: 'SaveSearch',
	components: { TripFilters },
	props: {
		isShown: Boolean,
		loadedSearch: Object,
		searchFilter: Object
	},
	data() {
		return {
			newSettings: {},
			tripTimeInterval: {},
			tripCriteria: {},
			ongoingSave: false,
			missingLabel: false,
		};
	},
	computed: {
		fromDateIsAfterToday: {
			get() {
				return this.tripTimeInterval.before ? (this.tripTimeInterval.before > 0) : false;
			},
			set(toggled) {
				if (toggled) {
					this.tripTimeInterval.before = Math.abs(this.tripTimeInterval.before).toString();
				} else {
					this.tripTimeInterval.before = (Math.abs(this.tripTimeInterval.before) * -1).toString();
				}
			},
		},
		toDateIsAfterToday: {
			get() {
				return this.tripTimeInterval.after ? (this.tripTimeInterval.after > 0) : true;
			},
			set(toggled) {
				if (toggled) {
					this.tripTimeInterval.after = Math.abs(this.tripTimeInterval.after).toString();
				} else {
					this.tripTimeInterval.after = (Math.abs(this.tripTimeInterval.after) * -1).toString();
				}
			},
		},
		before: {
			get() {
				return this.tripTimeInterval.before ? Math.abs(this.tripTimeInterval.before).toString() : '';
			},
			set(value) {
				this.tripTimeInterval.before = (this.fromDateIsAfterToday ? value : value * -1).toString();
			}
		},
		after: {
			get() {
				return this.tripTimeInterval.after ? Math.abs(this.tripTimeInterval.after).toString() : '';
			},
			set(value) {
				if (value) {
					this.tripTimeInterval.after = (this.toDateIsAfterToday ? value : value * -1).toString();
				} else {
					this.tripTimeInterval.after = value;
				}
			}
		},
	},
	methods: {
		...mapActions([
			'addSavedSearch',
			'updateSavedSearch',
			'deleteSavedSearch'
		]),
		processShown() {
			let { etd: { from, to } } = this.searchFilter;
			let before = this.diffFromToday(from);
			let after = this.diffFromToday(to);

			this.missingLabel = false;

			if (this.loadedSearch.id === -1) {
				this.newSettings = {
					...deepCopy(this.searchFilter),
					label: '',
					mainMenu: true,
					isPublic: false,
					autoSearch: true,
				};

				this.tripTimeInterval = {
					before,
					after,
					useAfter: Boolean(after),
				};

				this.tripCriteria = {
					fromLocation: Boolean(this.searchFilter.fromLocation.length),
					toLocation: Boolean(this.searchFilter.toLocation.length),
					tripType: Boolean(this.searchFilter.tripType.length),
					status: Boolean(this.searchFilter.status.length),
					cargoCarrier: Boolean(this.searchFilter.cargoCarrier.length)
				};
			} else {
				this.newSettings = {
					...deepCopy(this.loadedSearch),
					label: this.loadedSearch.label,
					mainMenu: this.loadedSearch.mainMenu,
					isPublic: this.loadedSearch.isPublic,
					autoSearch: this.loadedSearch.autoSearch,
				};
				this.tripTimeInterval = { ...this.loadedSearch.timeInterval, before, after };
				this.tripTimeInterval.useAfter = Boolean(this.tripTimeInterval.after);

				this.tripCriteria = {
					fromLocation: this.loadedSearch.fromLocation && this.loadedSearch.fromLocation.length > 0,
					toLocation: this.loadedSearch.toLocation && this.loadedSearch.toLocation.length > 0,
					tripType: this.loadedSearch.tripType && this.loadedSearch.tripType.length > 0,
					status: this.loadedSearch.status && this.loadedSearch.status.length > 0,
					cargoCarrier: this.loadedSearch.cargoCarrier && this.loadedSearch.cargoCarrier.length > 0
				};
			}
		},

		updateTripFilter(key, checked) {
			this.newSettings[key] = checked ? deepCopy(this.searchFilter[key]) : [];
		},
		diffFromToday(date) {
			if (date)
				return moment(date, storageDateFormat).startOf('day').diff(moment().startOf('day'), 'days').toString();
			return '';
		},
		async onSaveSearch() {
			if (this.ongoingSave) return;
			this.$notify({ clean: true });
			this.ongoingSave = true;

			if (!this.newSettings.label) {
				this.$notify({
					type: 'error',
					title: this.$t('noLabel'),
				});
				this.missingLabel = true;
				this.ongoingSave = false;
				return;
			}
			this.missingLabel = false;

			let { searchId } = this.$route.params;
			try {
				let searchOptions = {
					...this.newSettings,
					timeInterval: { ...this.tripTimeInterval }
				};

				if (searchId) await this.updateSavedSearch({ searchOptions, id: searchId, searchFilter: this.searchFilter });
				else {
					let { id } = await this.addSavedSearch({ searchOptions, searchFilter: this.searchFilter });
					this.$router.push({
						name: 'savedTripSearch',
						params: { searchId: id }
					});
				}

				this.$notify({
					type: 'success',
					title: `${this.$t('savedSearch')}: ${this.newSettings.label}`
				});
				this.$emit('close');
			} catch (e) {
				this.$notify({
					type: 'error',
					duration: -1,
					title: this.$te(e.message) ? this.$t(e.message) : this.$t('failedToSave'),
				});
			}
			this.ongoingSave = false;
		},
		async onDeleteSearch() {
			this.$notify({ clean: true });
			try {
				await this.deleteSavedSearch(this.$route.params.searchId);
				this.$emit('delete');
				this.$notify({
					type: 'success',
					title: `${this.$t('deletedSearch')}: ${this.newSettings.label}`
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					duration: -1,
					title: this.$t('failedToDelete')
				});
			}
		},
		onClose() {
			this.$notify({ clean: true });
			this.$emit('close');
		},
	}
};
</script>

<style lang="scss" scoped>
	@import '~@/components/evergreen/scss/colors';
	@import '~@/components/evergreen/scss/text';

.save-search-top {
	padding-bottom: 20px;
	border-bottom: solid 1px #e4e7eb;
}
.save-search-mid {
	padding-top: 10px;
	display: grid;
	grid-template-columns: 175px auto;
	grid-column-gap: 20px;
}
.save-search-time {
	padding-bottom: 15px;
	border-bottom: solid 1px #e4e7eb;
	display: grid;
	grid-template-rows: repeat(2, auto);
	grid-row-gap: 20px;
}
.time-interval {
	display: grid;
	grid-column-gap: 10px;
	grid-template-columns: auto max-content auto;
	grid-template-areas:
		"input1 checkbox input2"
		"switch1 ....... switch2";
	padding-bottom: 10px;
}

.save-search-criteria {
	padding-top: 20px;
	padding-bottom: 15px;
	border-bottom: solid 1px #e4e7eb;
	display: flex;
	flex-direction: column;
}

.search-criteria-label {
	user-select: none;
	font-size: 11px;
	font-weight: 600;
	line-height: 1.45;
	letter-spacing: 0.3px;
	color: get-color($text, 'muted');
	text-transform: uppercase;
	margin-bottom: 8px;
}
.search-criteria {
	@extend %text-small;
	@extend %font-families-ui;
	font-weight: 600;
	user-select: none;
	line-height: 20px;
	color: get-color($text, 'dark');
	box-sizing: border-box;
	&.disabled {
		color: get-color($text, 'muted');
	}
}
.search-criteria-description {
	@extend %text-small;
	@extend %font-families-ui;
	user-select: none;
	color: get-color($text, 'muted');
	box-sizing: border-box;
	margin-top: 0px;
	margin-bottom: 25px;
}
</style>
